// features/workstations/tabs/dashboard.js
// ═══════════════════════════════════════════════════════════
// DASHBOARD TAB — row-based smart grid + transient Add overlay
// ═══════════════════════════════════════════════════════════
// Reads rows + library from /api/workstations/widgets.
//
// Modes:
//   View mode  — clean dashboard, no edit chrome
//   Edit mode  — inline drag/resize/remove on each widget,
//                topbar shows [↻ Reset] [+ Add Widgets] [✓ Done]
//   Add mode   — overlay opens (right side); user multi-selects
//                widgets, clicks Add → widgets append → overlay
//                closes; back to Edit mode
// ═══════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useRef } from 'react';
import DashboardGrid from '../../../components/widgets/dashboard-grid';
import AddWidgetsOverlay from '../components/add-widgets-overlay';
import DashboardFocusMode from '../components/dashboard-focus-mode';

export default function DashboardTab({ workstation, userId, userRecord }) {
  // User's chosen AI name — falls through widget-card category label
  // ('jarvis-surface' \u2192 '<aiName> Surface') and anywhere else a
  // widget wants to personalize pronouns without leaking a hardcoded
  // 'Jarvis' into the UI.
  const userAiName = userRecord?.ai_name || null;
  const [rows, setRows] = useState([]);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Commit 74 — Focus / Grid mode toggle. Persisted per workstation
  // so different workstations remember their preferred layout.
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    const stored = window.localStorage.getItem(`shift_dashboard_mode:${workstation?.id || 'default'}`);
    return stored === 'focus' ? 'focus' : 'grid';
  });
  function setModePersisted(next) {
    setMode(next);
    if (typeof window !== 'undefined' && workstation?.id) {
      try {
        window.localStorage.setItem(`shift_dashboard_mode:${workstation.id}`, next);
      } catch { /* ignore */ }
    }
  }

  const pendingAdjustRef = useRef(null);

  // ─── Load ───
  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ workstation_id: workstation.id, user_id: userId });
      const res = await fetch(`/api/workstations/widgets?${qs}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
      setLibrary(data.library || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  // ─── POST helper ───
  const postAction = useCallback(async (body) => {
    const res = await fetch('/api/workstations/widgets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, workstation_id: workstation.id, ...body }),
    });
    const data = res.ok ? await res.json() : null;
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    return data;
  }, [userId, workstation?.id]);

  // ─── Add multiple widgets (sequential) ───
  const handleAddMultiple = useCallback(async (widgetKeys = []) => {
    if (!Array.isArray(widgetKeys) || widgetKeys.length === 0) return;
    let lastData = null;
    for (const key of widgetKeys) {
      try {
        lastData = await postAction({ action: 'add_widget', widget_key: key });
      } catch (e) {
        console.warn('[dashboard] add failed for', key, e?.message);
      }
    }
    if (lastData) {
      setRows(lastData.rows);
      setLibrary(lastData.library);
    } else {
      // Recover by full reload
      load();
    }
  }, [postAction, load]);

  // ─── Remove widget (optimistic) ───
  const handleRemove = useCallback(async (widgetKey) => {
    setRows((prev) => {
      const out = [];
      for (const row of prev) {
        const remaining = row.widgets.filter((w) => w.key !== widgetKey);
        if (remaining.length === 0) continue;
        if (remaining.length === row.widgets.length) { out.push(row); continue; }
        const n = remaining.length;
        const each = Math.floor(12 / n);
        const extra = 12 - each * n;
        out.push({
          ...row,
          widgets: remaining.map((w, i) => ({ ...w, share: each + (i < extra ? 1 : 0) })),
        });
      }
      return out;
    });
    try {
      const data = await postAction({ action: 'remove_widget', widget_key: widgetKey });
      setRows(data.rows);
      setLibrary(data.library);
    } catch (e) {
      setError(e?.message || 'Remove failed');
      load();
    }
  }, [postAction, load]);

  // ─── Divider adjust (debounced persist) ───
  const handleAdjustShare = useCallback((rowId, leftKey, rightKey, delta) => {
    if (!delta) return;
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      const ws = r.widgets.map((w) => ({ ...w }));
      const li = ws.findIndex((w) => w.key === leftKey);
      const ri = ws.findIndex((w) => w.key === rightKey);
      if (li < 0 || ri < 0) return r;
      const newLeft = ws[li].share + delta;
      const newRight = ws[ri].share - delta;
      if (newLeft < 3 || newRight < 3) return r;
      ws[li] = { ...ws[li], share: newLeft };
      ws[ri] = { ...ws[ri], share: newRight };
      return { ...r, widgets: ws };
    }));
    if (pendingAdjustRef.current) clearTimeout(pendingAdjustRef.current);
    pendingAdjustRef.current = setTimeout(() => {
      setRows((current) => {
        postAction({ action: 'save_rows', rows: current }).catch((e) => {
          console.warn('[dashboard] adjust persist failed:', e?.message);
        });
        return current;
      });
    }, 400);
  }, [postAction]);

  // ─── Drag-end from the grid ───
  const handleDragEnd = useCallback(async (event) => {
    try {
      if (event.type === 'move_to_new_row') {
        const data = await postAction({
          action: 'move_to_new_row',
          widget_key: event.widgetKey,
          insert_row_index: event.insertRowIndex,
        });
        setRows(data.rows);
        setLibrary(data.library);
      } else if (event.type === 'insert_in_row') {
        const data = await postAction({
          action: 'insert_in_row',
          widget_key: event.widgetKey,
          row_id: event.rowId,
          position: event.position,
        });
        setRows(data.rows);
        setLibrary(data.library);
      } else if (event.type === 'save_rows') {
        setRows(event.rows);
        const data = await postAction({ action: 'save_rows', rows: event.rows });
        setRows(data.rows);
        setLibrary(data.library);
      }
    } catch (e) {
      setError(e?.message || 'Drag failed');
      load();
    }
  }, [postAction, load]);

  // ─── Reset ───
  const handleResetLayout = useCallback(async () => {
    if (typeof window !== 'undefined' && !window.confirm('Reset layout to defaults? Widget positions will be recalculated.')) {
      return;
    }
    try {
      const data = await postAction({ action: 'reset' });
      setRows(data.rows);
      setLibrary(data.library);
    } catch (e) {
      setError(e?.message || 'Reset failed');
    }
  }, [postAction]);

  // ─── Render ───
  if (loading && rows.length === 0) {
    return (
      <div className="ws-tab-content">
        <div className="ws-dashboard-empty-body">Loading dashboard…</div>
      </div>
    );
  }

  const totalWidgetCount = rows.reduce((s, r) => s + r.widgets.length, 0);

  return (
    <div className={`ws-tab-content ws-dashboard-tab ${editing ? 'is-editing' : ''}`}>
      <div className="ws-dashboard-header">
        <div className="ws-dashboard-header-title-wrap">
          <h2 className="ws-dashboard-header-title">Dashboard</h2>
          <span className="ws-dashboard-header-count">
            {totalWidgetCount} widget{totalWidgetCount === 1 ? '' : 's'} · {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* Topbar buttons — different in view vs edit mode */}
        <div className="ws-dashboard-header-actions">
          {/* Commit 74 — Focus / Grid mode toggle. Hidden in edit
              mode since editing only makes sense in Grid (drag/drop
              of widgets). */}
          {!editing && (
            <div className="ws-dashboard-mode-toggle">
              <button
                type="button"
                className={`ws-dashboard-mode-btn ${mode === 'focus' ? 'is-active' : ''}`}
                onClick={() => setModePersisted('focus')}
                title="Single widget at a time + chat alongside"
              >
                ◉ Focus
              </button>
              <button
                type="button"
                className={`ws-dashboard-mode-btn ${mode === 'grid' ? 'is-active' : ''}`}
                onClick={() => setModePersisted('grid')}
                title="All widgets in a scrolling grid"
              >
                ▦ Grid
              </button>
            </div>
          )}
          {!editing && mode === 'grid' && (
            <button
              type="button"
              className="ws-dashboard-customize-btn"
              onClick={() => setEditing(true)}
              title="Edit your dashboard layout"
            >
              ✦ Customize
            </button>
          )}
          {editing && (
            <>
              <button
                type="button"
                className="ws-dashboard-secondary-btn"
                onClick={handleResetLayout}
                title="Reset all widget positions to defaults"
              >
                ↻ Reset Layout
              </button>
              <button
                type="button"
                className="ws-dashboard-secondary-btn"
                onClick={() => setAddOpen(true)}
                title="Add widgets to the dashboard"
              >
                + Add Widgets
              </button>
              <button
                type="button"
                className="ws-dashboard-customize-btn is-active"
                onClick={() => { setEditing(false); setAddOpen(false); }}
                title="Exit edit mode"
              >
                ✓ Done
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="ws-dashboard-error">{error}</div>}

      {/* Dashboard body — Grid (current) or Focus (Commit 74) */}
      <div className="ws-dashboard-grid-wrap">
        {(mode === 'grid' || editing) ? (
          <DashboardGrid
            rows={rows}
            workstation={workstation}
            userId={userId}
            userAiName={userAiName}
            editing={editing}
            onRemoveWidget={handleRemove}
            onAdjustShare={handleAdjustShare}
            onDragEnd={handleDragEnd}
          />
        ) : (
          <DashboardFocusMode
            rows={rows}
            library={library}
            workstation={workstation}
            userId={userId}
            userAiName={userAiName}
          />
        )}
      </div>

      {/* Transient Add overlay — only when user clicks + Add Widgets */}
      <AddWidgetsOverlay
        open={addOpen}
        onClose={() => setAddOpen(false)}
        library={library}
        onAddMultiple={handleAddMultiple}
      />
    </div>
  );
}
